/**
 * German Card Comparison Adapter
 * Converts Supabase DB cards to German comparison format with rich FinanceAds data
 */

import { Database } from "@/integrations/supabase/types";
import { GermanCompareCard } from "@/types/cardDE";

type DbCard = Database['public']['Tables']['cards']['Row'];

/**
 * Format percent with comma (German style): 17.29 → "17,29 %"
 */
const fmtPct = (n: number | null): string => {
  if (n == null) return '–';
  return `${n.toFixed(2).replace('.', ',')} %`;
};

/**
 * Format EUR with comma: 49.99 → "49,99 €"
 * Zero → "Gratis"
 */
const fmtEUR = (n: number | null): string => {
  if (n == null) return '–';
  if (n === 0) return 'Gratis';
  const formatted = n.toFixed(2).replace('.', ',');
  const parts = formatted.split(',');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u202F'); // thin space
  return `${parts.join(',')} €`;
};

/**
 * Build data-driven pros from benefits + synthetic rules
 */
const buildPros = (card: DbCard, displayConfig: any): string[] => {
  const pros: string[] = [];
  
  // From benefits array (first 3)
  if (card.benefits && Array.isArray(card.benefits)) {
    pros.push(...card.benefits.slice(0, 3));
  }
  
  // Synthetic pros based on card properties
  if (card.annual_fee === 0) {
    pros.push('Dauerhaft gebührenfrei');
  }
  
  if (card.foreign_tx_fee_percent === 0) {
    pros.push('0 % Auslandseinsatzentgelt');
  }
  
  if (card.travel_insurance || card.travel_health_insurance) {
    pros.push('Reiseversicherung inklusive');
  }
  
  if (card.card_type === 'credit') {
    pros.push('Teilzahlung möglich');
  }
  
  // Check for discounts in display_config
  const hasDiscounts = displayConfig?.chips?.some((c: any) => 
    c.label?.includes('Rabatt') || c.label?.includes('Discount')
  );
  if (hasDiscounts) {
    pros.push('Rabatte auf Reisen und Mietwagen');
  }
  
  if (card.cashback_program) {
    pros.push('Cashback-Programm');
  }
  
  if (card.bonus_program) {
    pros.push('Bonuspunkte sammeln');
  }
  
  // Limit to 5 most important
  return pros.slice(0, 5);
};

/**
 * Build data-driven cons from drawbacks + synthetic rules
 */
const buildCons = (card: DbCard): string[] => {
  const cons: string[] = [];
  
  // From drawbacks array
  if (card.drawbacks && Array.isArray(card.drawbacks)) {
    cons.push(...card.drawbacks.slice(0, 2));
  }
  
  // Synthetic cons
  if (card.card_type === 'credit' && card.apr_effective && card.apr_effective > 20) {
    cons.push('Hoher Sollzins bei Teilzahlung');
  }
  
  if (!card.nfc_contactless) {
    cons.push('Kein kontaktloses Zahlen');
  }
  
  if (!card.travel_insurance && !card.cashback_program && !card.bonus_program) {
    cons.push('Keine Zusatzleistungen');
  }
  
  if (card.annual_fee && card.annual_fee > 50) {
    cons.push(`Jahresgebühr: ${fmtEUR(card.annual_fee)}`);
  }
  
  // Return at least one con, limit to 4
  return cons.length > 0 ? cons.slice(0, 4) : ['–'];
};

/**
 * Extract discount text from description or display_config
 */
const extractDiscountText = (card: DbCard, displayConfig: any): string | null => {
  // Check display_config chips for discount info
  const discountChip = displayConfig?.chips?.find((c: any) => 
    c.label?.includes('Rabatt') || c.label?.includes('Discount')
  );
  
  if (discountChip?.tooltip) {
    return discountChip.tooltip;
  }
  
  // Check rental_car_discount_text or travel_discount_text
  if (card.rental_car_discount_text) {
    return card.rental_car_discount_text;
  }
  
  if (card.travel_discount_text) {
    return card.travel_discount_text;
  }
  
  return null;
};

/**
 * Convert Supabase DB card to German Comparison Card format
 */
export function adaptSupabaseCardToGermanCompareCard(
  dbCard: DbCard,
  isSponsored: boolean = false,
  sponsoredLabel: string = 'Anzeige'
): GermanCompareCard {
  // Cast to any for German-specific fields not in base type
  const card = dbCard as any;
  
  // Parse display_config
  const displayConfig = card.display_config || {};
  
  // Determine if prepaid
  const isPrepaid = card.is_prepaid || false;
  
  // APR text (prepaid cards show "–")
  const aprEffectiveText = isPrepaid ? '–' : (displayConfig.aprText || fmtPct(dbCard.apr_effective));
  
  // Annual fee text
  const annualFeeText = displayConfig.annualFeeText || fmtEUR(dbCard.annual_fee);
  
  // Foreign transaction fee
  const foreignTxFeeText = dbCard.foreign_tx_fee_percent != null 
    ? fmtPct(dbCard.foreign_tx_fee_percent)
    : null;
  
  // Extract insurance booleans
  const insurance = {
    health: dbCard.travel_health_insurance || false,
    accident: dbCard.travel_accident_insurance || false,
    cancellation: dbCard.travel_cancellation_insurance || false,
    baggage: dbCard.travel_baggage_insurance || false,
  };
  
  // Extract features
  const features = {
    hasDiscounts: !!(dbCard.travel_discount_text || dbCard.rental_car_discount_text || 
                     displayConfig?.chips?.some((c: any) => c.label?.includes('Rabatt'))),
    discountText: extractDiscountText(dbCard, displayConfig),
    hotline: card.hotline_available || false,
    nfc: dbCard.nfc_contactless || false,
    withAccount: card.current_account_included || false,
    liability: null, // Not in feed yet
    cashback: dbCard.cashback_program || false,
    bonusProgram: dbCard.bonus_program || false,
  };
  
  // Network mapping
  const networkMap: Record<string, string> = {
    'visa': 'Visa',
    'mastercard': 'Mastercard',
    'american_express': 'American Express',
  };
  
  // Card type mapping
  const typeMap: Record<string, string> = {
    'credit': 'Kreditkarte',
    'charge': 'Charge-Karte',
    'debit': 'Debitkarte',
    'prepaid': 'Prepaid-Karte',
  };
  
  return {
    id: dbCard.id,
    slug: dbCard.slug,
    name: dbCard.display_name || dbCard.name,
    bank: dbCard.issuer,
    logo: dbCard.logo_url || '',
    network: networkMap[dbCard.network] || dbCard.network || 'Visa',
    cardTier: card.card_tier || null,
    cardType: card.card_type ? typeMap[card.card_type] : null,
    isPrepaid,
    
    // Costs
    aprEffective: dbCard.apr_effective,
    aprEffectiveText,
    annualFee: dbCard.annual_fee || 0,
    annualFeeText,
    foreignTxFeeText,
    atmDomesticText: dbCard.atm_domestic_text,
    atmEurozoneText: dbCard.atm_eurozone_text,
    atmInternationalText: dbCard.atm_international_text,
    
    // Insurance
    insurance,
    
    // Features
    features,
    
    // Requirements
    minAge: dbCard.age_min || 18,
    minIncome: dbCard.income_min,
    
    // Pros/Cons (data-driven)
    pros: buildPros(dbCard, displayConfig),
    cons: buildCons(dbCard),
    
    // Meta
    isSponsored,
    sponsoredLabel,
    lastUpdated: new Date(dbCard.updated_at || dbCard.created_at || Date.now()).toISOString().split('T')[0],
    applyUrl: dbCard.apply_url,
  };
}
